import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { X, ListChecks } from "lucide-react";
import { formatDateTimeAR } from "@/lib/i18n";

/** Fix BV (2026-11-04): cuándo se cargó cada turno del cronograma y sus modificaciones (pedido del cliente). */
const overlayStyle = { position: "fixed", inset: 0, background: "rgba(10,10,10,0.7)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 200 };
const modalStyle = { background: "#fff", borderRadius: 12, padding: 18, width: "100%", maxWidth: 720, maxHeight: "88vh", overflowY: "auto" };
const KIND_LABEL = { edicion: "Modificó", franco: "Pasó a franco", cobertura: "Cambió por cobertura", restaurado: "Restauró" };
const dm = (d) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}` : "");

function fmtVal(field, v) {
  if (v === null || v === undefined || v === "") return "—";
  if (field === "date") return `${v.slice(8, 10)}/${v.slice(5, 7)}/${v.slice(0, 4)}`;
  if (field === "is_day_off") return v ? "Sí" : "No";
  if (field === "day_off_half") return v === "manana" ? "Mañana" : v === "tarde" ? "Tarde" : "—";
  return String(v);
}

export function changesText(e) {
  return (e.changes || []).map((c) => `${c.label}: ${fmtVal(c.field, c.from)} → ${fmtVal(c.field, c.to)}`).join("; ");
}

/** Debajo de cada turno, en el detalle del día. */
export function ScheduleTrail({ a }) {
  const [open, setOpen] = useState(false);
  const log = a.change_log || [];
  return (
    <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 4 }} data-testid={`schedule-trail-${a.id}`}>
      <div>
        Cargado {a.created_at ? `el ${formatDateTimeAR(a.created_at)}` : "(sin fecha registrada)"}{a.created_by_name ? ` por ${a.created_by_name}` : ""}
        {a.updated_at ? ` · Última modificación el ${formatDateTimeAR(a.updated_at)}${a.updated_by_name ? ` por ${a.updated_by_name}` : ""}` : ""}
      </div>
      {log.length > 0 && (
        <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`schedule-trail-toggle-${a.id}`}
          style={{ border: "none", background: "transparent", padding: 0, marginTop: 2, color: "#0A0A0A", fontSize: 11, fontWeight: 700, cursor: "pointer", textDecoration: "underline" }}>
          {open ? "Ocultar modificaciones" : `Ver modificaciones (${log.length})`}
        </button>
      )}
      {open && log.slice().reverse().map((e, i) => (
        <div key={i} style={{ marginTop: 3, paddingLeft: 8, borderLeft: "2px solid var(--invict-bone-3)" }}>
          <b>{formatDateTimeAR(e.at)}</b> · {e.by_name} · {KIND_LABEL[e.kind] || "Modificó"}{changesText(e) ? `: ${changesText(e)}` : ""}{e.note ? ` · ${e.note}` : ""}
        </div>
      ))}
    </div>
  );
}

function describe(ev) {
  if (ev.kind === "carga") {
    if (ev.count > 1) {
      return `Cargó ${ev.count} turnos${ev.client_name ? ` en ${ev.client_name}` : ""}, del ${dm(ev.date_from)} al ${dm(ev.date_to)}${ev.note ? " (repetido del mes anterior)" : ""}`;
    }
    const where = [ev.client_name, ev.site_name].filter(Boolean).join(" · ");
    return `Cargó el turno de ${ev.user_name} del ${dm(ev.date)}: ${ev.detail}${where ? ` · ${where}` : ""}`;
  }
  const where = [ev.client_name, ev.site_name].filter(Boolean).join(" · ");
  return `${KIND_LABEL[ev.kind] || "Modificó"} el turno de ${ev.user_name} del ${dm(ev.date)}${where ? ` · ${where}` : ""}`;
}

/** Botón "Historial de cambios" del Cronograma: el mes y los filtros que se están viendo. */
export function ScheduleChangesPanel({ month, clientId, siteId, clientName, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    const params = { month };
    if (clientId) params.client_id = clientId;
    if (siteId) params.site_id = siteId;
    api.get("/schedule/changes", { params })
      .then(({ data }) => setRows(data || []))
      .catch((e) => { toast.error(formatApiError(e)); setRows([]); });
  }, [month, clientId, siteId]);

  return (
    <div style={overlayStyle} onClick={onClose} data-testid="schedule-changes-overlay">
      <div style={modalStyle} onClick={(e) => e.stopPropagation()} data-testid="schedule-changes-panel">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 900, display: "flex", alignItems: "center", gap: 6 }}>
            <ListChecks size={16} /> Historial de cambios · {month.slice(5, 7)}/{month.slice(0, 4)}{clientName ? ` · ${clientName}` : " · todos los objetivos"}
          </h3>
          <button onClick={onClose} style={{ border: "none", background: "transparent", cursor: "pointer" }} data-testid="schedule-changes-close"><X size={18} /></button>
        </div>
        <p style={{ fontSize: 11, color: "var(--invict-text-muted)", margin: "0 0 12px" }}>
          Cuándo se cargaron los turnos y cada modificación, de lo más nuevo a lo más viejo. Los turnos borrados están en "Turnos borrados recientemente".
        </p>
        {rows === null && <div className="skel" style={{ height: 80 }} />}
        {rows && rows.length === 0 && <div style={{ fontSize: 12, color: "var(--invict-text-muted)" }} data-testid="schedule-changes-empty">Sin cargas ni modificaciones registradas en este mes.</div>}
        {rows && rows.map((ev, i) => (
          <div key={i} style={{ padding: "8px 0", borderTop: "1px solid var(--invict-bone-3)", fontSize: 12 }} data-testid={`schedule-change-${i}`}>
            <div><b>{formatDateTimeAR(ev.at)}</b> · {ev.by_name || "—"}</div>
            <div style={{ color: "#0A0A0A" }}>{describe(ev)}</div>
            {ev.kind !== "carga" && changesText(ev) && <div style={{ color: "var(--invict-text-muted)" }}>{changesText(ev)}</div>}
            {ev.kind !== "carga" && ev.note && <div style={{ color: "var(--invict-text-muted)", fontStyle: "italic" }}>{ev.note}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
